import {
  getCookie,
  getCookieDomain,
  getEnvironmentPrefixedCookieName,
  setCookie,
} from "@theme/utilities";
import { getPageGlobalProps } from "@theme/analytics.util";

const defaultSierraConfig = {
  customGreeting:
    "Hi, I’m Adina, your personal BODi virtual assistant. I can help you find your perfect fitness program. Just let me know what you’re looking for— feel free to include things like your fitness goals, experience level, or favorite workout style so I can make the best recommendation.",
  persistence: "cookie",
  cookieDomain: getCookieDomain(),
  cookieName: getEnvironmentPrefixedCookieName("bodi-sierra-chat-session"),
  colors: {
    background: "#ffffff",
    text: "#161819",
    border: "#A09EA3",
    titleBar: "#161819",
    titleBarText: "#ffffff",
    assistantBubble: "#EDFAFF",
    assistantBubbleText: "#164646",
    userBubble: "#d8dbdf",
    userBubbleText: "#4a4e5a",
  },
  display: "corner",
  hideOnClose: true,
  /**
   * Handler for when the Sierra chat is opened.
   * Dispatches a "sierra:open" event with the provided options as detail.
   * @param {any} opts - Options or data related to the open event.
   */
  onOpen(opts) {
    window.dispatchEvent(new CustomEvent("sierra:open", { detail: opts }));
    sessionStorage.setItem(sierraChatOpenState, "true");
    window.amplitude?.track("open chat", {
      "is new conversation": opts.isNewConversation,
      ...getPageGlobalProps(location.href),
    });
  },
  /**
   * Handler for when the Sierra chat is closed.
   * Dispatches a "sierra:close" event.
   */
  onClose() {
    window.dispatchEvent(new CustomEvent("sierra:close"));
    sessionStorage.removeItem(sierraChatOpenState);
    removeChatHashFromUrl();
  },
  /**
   * Handler for when a new conversation is started in Sierra.
   * Dispatches a "sierra:conversation-start" event with the provided options as detail.
   * @param {any} opts - Options or data related to the conversation start event.
   */
  onConversationStart(opts) {
    window.dispatchEvent(new CustomEvent("sierra:conversation-start", { detail: opts }));
    window.amplitude?.track("start conversation", {
      "conversation id": opts.conversationID,
      ...getPageGlobalProps(location.href),
    });
  },
};

/**
 * Removes "#chat" from the URL hash (without reloading) after the chat closes.
 */
function removeChatHashFromUrl() {
  if (window.location.hash !== "#chat") return;

  const nextUrl = `${window.location.pathname}${window.location.search}`;
  history.replaceState(history.state, document.title, nextUrl);
}

/**
 * Sets the global Sierra configuration object on the window.
 * This configuration is used by the Sierra chat widget.
 */
function setSierraConfig() {
  window.sierraConfig = {
    ...defaultSierraConfig,
  };
}

function injectSierraWidget() {
  const sierraenabled = window.BODi.configs.sierra.enabled;
  if (!sierraenabled) {
    return;
  }

  const scriptUrl = window.BODi.configs.sierra.apiUrl;
  if (!scriptUrl) {
    console.warn("Sierra widget: missing script URL in config");
    return;
  }

  if (document.querySelector("script[data-sierra-widget]")) {
    return;
  }

  const script = document.createElement("script");
  script.src = scriptUrl;
  script.async = true;
  script.crossOrigin = "anonymous";
  script.dataset.sierraWidget = "true";

  script.addEventListener("error", () => {
    console.error("Sierra widget: failed to load script", { scriptUrl });
  });

  script.addEventListener("load", () => {
    createSierraTrigger();
    restoreChatOpenState();
  });

  document.head.appendChild(script);
}

function initializeSierra() {
  setSierraConfig();
  injectSierraWidget();
  setupHashChangeListener();
}

/**
 * Sets up hash change listener to open chat when #chat hash is present
 */
function setupHashChangeListener() {
  // Check initial hash on load
  if (window.location.hash === "#chat") {
    window.sierra?.openChatModal?.();
  }

  // Listen for hash changes
  window.addEventListener("hashchange", () => {
    if (window.location.hash === "#chat") {
      window.sierra?.openChatModal?.();
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeSierra);
} else {
  initializeSierra();
}

/**
 * Creates and injects the Sierra chat trigger button and its wrapper into the DOM,
 * along with a proactive banner. The trigger button opens the Sierra chat modal
 * when clicked. The function ensures only one trigger exists, styles the button
 * and wrapper, and manages their visibility based on Sierra chat open/close events.
 * Also sets up accessibility attributes and event listeners for interaction and focus.
 */
function createSierraTrigger() {
  const existingTrigger = document.querySelector("[data-sierra-trigger]");
  if (existingTrigger || typeof window.sierra?.openChatModal !== "function") {
    return;
  }

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.dataset.sierraTrigger = "true";
  trigger.setAttribute("aria-haspopup", "dialog");
  trigger.setAttribute("aria-label", "Open chat");

  const wrapper = document.createElement("div");
  wrapper.dataset.sierraTriggerWrapper = "true";

  Object.assign(wrapper.style, {
    position: "fixed",
    right: "1rem",
    bottom: "1rem",
    zIndex: "1000",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "0.5rem",
    transition: "transform 0.3s ease",
  });

  // Hide chat widget on mobile devices
  const mobileMediaQuery = window.matchMedia('(max-width: 768px)');
  
  function handleMobileView() {
    const isMobile = mobileMediaQuery.matches;
    if (isMobile) {
      // Mobile view - hide chat widget
      wrapper.style.display = 'none';
    } else {
      // Desktop view - show chat widget (unless chat is open)
      const isChatOpen = sessionStorage.getItem('bodi-sierra-chat-open') === 'true';
      if (!isChatOpen) {
        wrapper.style.display = 'flex';
      }
    }
  }

  // Apply initial mobile check
  handleMobileView();
  
  // Listen for screen size changes
  mobileMediaQuery.addListener(handleMobileView);

  const banner = createProactiveBanner();

  Object.assign(trigger.style, {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.25rem",
    padding: "8px 16px",
    borderRadius: "9999px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "var(--bodi-cool-gray-700)",
    color: "#ffffff",
    fontSize: "1rem",
    fontFamily: 'Heebo, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeight: "600",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
    transition: "background-color 150ms ease, transform 150ms ease",
  });

  trigger.addEventListener("mouseenter", () => {
    trigger.style.backgroundColor = "var(--bodi-black)";
  });

  trigger.addEventListener("mouseleave", () => {
    trigger.style.backgroundColor = "var(--bodi-cool-gray-700)";
  });

  trigger.addEventListener("focus", () => {
    trigger.style.outline = "2px solid #EDFAFF";
    trigger.style.outlineOffset = "2px";
  });

  trigger.addEventListener("blur", () => {
    trigger.style.outline = "none";
  });

  const icon = createChatIcon();
  const text = document.createElement("span");
  text.textContent = "Chat";

  trigger.appendChild(icon);
  trigger.appendChild(text);

  trigger.addEventListener("click", () => {
    window.sierra?.openChatModal();
  });

  window.addEventListener("sierra:open", () => {
    wrapper.style.display = "none";
    hideProactiveBanner(banner);
  });

  window.addEventListener("sierra:close", () => {
    wrapper.style.display = "flex";
  });

  // Listen for sticky footer custom events to move chat widget
  const handleStickyFooterShow = () => {
    // Always measure the footer height directly
    let footerHeight = 60; // Default fallback
    
    const stickyFooter = document.querySelector('.sticky-footer, #sticky-footer');
    if (stickyFooter) {
      const rect = stickyFooter.getBoundingClientRect();
      footerHeight = rect.height || 60;
    }
    
    wrapper.style.transform = `translateY(-${footerHeight}px)`;
  };

  const handleStickyFooterHide = () => {
    wrapper.style.transform = 'translateY(0)';
  };

  // Listen for custom footer events
  document.addEventListener('stickyFooter:show', handleStickyFooterShow);
  document.addEventListener('stickyFooter:hide', handleStickyFooterHide);
  document.addEventListener('stickyFooter:resize', handleStickyFooterShow);

  // Check initial state
  const checkInitialFooterState = () => {
    const stickyFooter = document.querySelector('.sticky-footer, #sticky-footer');
    if (stickyFooter && stickyFooter.classList.contains('show')) {
      handleStickyFooterShow();
    }
  };

  // Check initial state after a short delay to ensure footer is initialized
  setTimeout(checkInitialFooterState, 100);

  wrapper.appendChild(banner.element);
  wrapper.appendChild(trigger);
  document.body.appendChild(wrapper);

  setupProactiveBanner(banner);
}

function createChatIcon() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");
  svg.setAttribute("viewBox", "0 0 20 20");
  svg.setAttribute("aria-hidden", "true");

  const path1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path1.setAttribute("fill-rule", "evenodd");
  path1.setAttribute("clip-rule", "evenodd");
  path1.setAttribute("fill", "#FFFFFF");
  path1.setAttribute(
    "d",
    "M17.4977 9.29362C17.4992 9.35647 17.5 9.41952 17.5 9.48276C17.5 13.9576 13.6259 17.4655 9 17.4655C8.19568 17.4655 7.41572 17.3604 6.67562 17.1633C5.59534 17.5147 4.41192 17.8303 3.47985 18.0624C2.96809 18.1898 2.52723 18.2933 2.21386 18.365C2.05711 18.4009 1.93207 18.4288 1.84579 18.4479L1.74628 18.4698L1.71995 18.4755L1.71011 18.4776C1.29825 18.5661 0.8751 18.3871 0.6519 18.0298C0.428706 17.6725 0.453337 17.2137 0.7135 16.8824C1.22982 16.2248 1.71458 15.2068 2.05041 14.081C1.07772 12.7861 0.5 11.1999 0.5 9.48276C0.5 5.0079 4.37408 1.5 9 1.5C9.684 1.5 10.3515 1.57669 10.9925 1.72185C10.7205 2.31878 10.5521 2.97285 10.5103 3.66111C10.0274 3.55588 9.522 3.5 9 3.5C5.34165 3.5 2.5 6.24467 2.5 9.48276C2.5 10.8809 3.02071 12.1725 3.90747 13.2C4.12082 13.4472 4.20004 13.783 4.11966 14.0995C3.9567 14.7411 3.74352 15.3869 3.48781 15.9973C4.38869 15.7653 5.42414 15.4771 6.33352 15.1697C6.53194 15.1026 6.74642 15.0995 6.94669 15.1609C7.59042 15.3581 8.28044 15.4655 9 15.4655C12.6584 15.4655 15.5 12.7208 15.5 9.48276V9.47758C15.6647 9.49242 15.8315 9.5 16 9.5C16.5192 9.5 17.0215 9.42807 17.4977 9.29362Z"
  );

  const path2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path2.setAttribute(
    "d",
    "M16 7.5C17.933 7.5 19.5 5.933 19.5 4C19.5 2.067 17.933 0.5 16 0.5C14.067 0.5 12.5 2.067 12.5 4C12.5 5.933 14.067 7.5 16 7.5Z"
  );
  path2.setAttribute("fill", "#BDF987");

  svg.appendChild(path1);
  svg.appendChild(path2);

  return svg;
}

const proactiveBanner = {
  text: "👋 Hi, I'm a virtual agent. How can I help?",
  showAfterSeconds: 3,
  hideAfterSeconds: 10,
  cooldownMinutes: 30,
};

const sierraLastBannerShown = "bodi-sierra-last-banner-shown";
const sierraChatOpenState = "bodi-sierra-chat-open";

function createProactiveBanner() {
  const element = document.createElement("p");
  Object.assign(element.style, {
    display: "none",
    margin: "0 0 0.5rem 0",
    padding: "0.5rem 1rem",
    borderRadius: "9999px",
    backgroundColor: "#4a4e5a",
    color: "#ffffff",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    fontSize: "0.9375rem",
    textAlign: "center",
    whiteSpace: "nowrap",
  });

  element.textContent = proactiveBanner.text;

  return {
    element,
    showTimer: null,
    hideTimer: null,
  };
}

/**
 * Sets up the proactive banner to display after a delay, hide after a set time, and respect a cooldown period.
 * @param {Object} banner - The banner object containing the element and timer references.
 * @param {HTMLElement} banner.element - The DOM element for the banner.
 * @param {number|null} banner.showTimer - Reference to the show timer.
 * @param {number|null} banner.hideTimer - Reference to the hide timer.
 */
function setupProactiveBanner(banner) {
  const lastBannerShown = getCookie(sierraLastBannerShown);
  if (lastBannerShown) {
    return;
  }

  banner.showTimer = window.setTimeout(() => {
    showProactiveBanner(banner);
    setCookie(sierraLastBannerShown, new Date().toISOString(), {
      maxAge: proactiveBanner.cooldownMinutes * 60,
    });

    banner.hideTimer = window.setTimeout(() => {
      hideProactiveBanner(banner);
    }, proactiveBanner.hideAfterSeconds * 1000);
  }, proactiveBanner.showAfterSeconds * 1000);

  window.addEventListener("beforeunload", () => {
    if (banner.showTimer) {
      clearTimeout(banner.showTimer);
    }
    if (banner.hideTimer) {
      clearTimeout(banner.hideTimer);
    }
  });
}

/**
 * Shows the proactive banner by setting its display style to "inline-flex".
 * @param {Object} banner - The banner object containing the element.
 * @param {HTMLElement} banner.element - The DOM element for the banner.
 */
function showProactiveBanner(banner) {
  banner.element.style.display = "inline-flex";
}

/**
 * Hides the proactive banner by setting its display style to "none" and clears the hide timer if set.
 * @param {Object} banner - The banner object containing the element and hideTimer reference.
 * @param {HTMLElement} banner.element - The DOM element for the banner.
 * @param {number|null} banner.hideTimer - Reference to the hide timer.
 */
function hideProactiveBanner(banner) {
  banner.element.style.display = "none";
  if (banner.hideTimer) {
    clearTimeout(banner.hideTimer);
    banner.hideTimer = null;
  }
}

/**
 * Restores the Sierra chat open state from sessionStorage.
 * If the chat was open before navigation, it will be reopened automatically.
 */
function restoreChatOpenState() {
  const wasOpen = sessionStorage.getItem(sierraChatOpenState) === "true";
  if (!wasOpen) return;

  // Wait for Sierra trigger to be created, indicating widget is ready
  const checkSierraReady = () => {
    const trigger = document.querySelector("[data-sierra-trigger]");
    if (trigger && typeof window.sierra?.openChatModal === "function") {
      window.sierra.openChatModal();
    } else {
      // Retry with requestAnimationFrame for better performance
      requestAnimationFrame(checkSierraReady);
    }
  };

  checkSierraReady();
}
